import { useState, useEffect, useRef, useCallback } from 'react';

export type PomodoroPhase = 'work' | 'shortBreak' | 'longBreak';

export interface PomodoroProject {
  id: string;
  project_name: string;
  project_code: string;
}

export interface WorkSessionInfo {
  project: PomodoroProject | null;
  startTime: string; // HH:MM
  endTime: string;   // HH:MM
  date: string;      // YYYY-MM-DD
  durationMinutes: number;
}

export interface PomodoroSettings {
  workDuration: number; // minutes
  shortBreakDuration: number;
  longBreakDuration: number;
  longBreakInterval: number; // every N work sessions
  autoStartBreaks: boolean;
  autoStartWork: boolean;
  soundEnabled: boolean;
}

interface PomodoroState {
  phase: PomodoroPhase;
  sessionsCompleted: number;
  totalFocusSeconds: number;
  lastActiveDate: string; // YYYY-MM-DD
}

const DEFAULT_SETTINGS: PomodoroSettings = {
  workDuration: 25,
  shortBreakDuration: 5,
  longBreakDuration: 15,
  longBreakInterval: 4,
  autoStartBreaks: false,
  autoStartWork: false,
  soundEnabled: true,
};

const SETTINGS_KEY = 'pomodoro-settings';
const STATE_KEY = 'pomodoro-state';
const PROJECT_KEY = 'pomodoro-project';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function loadSettings(): PomodoroSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch { /* use defaults */ }
  return { ...DEFAULT_SETTINGS };
}

function loadState(): PomodoroState {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (raw) {
      const s = JSON.parse(raw) as PomodoroState;
      // Daily reset
      if (s.lastActiveDate !== today()) {
        return { phase: 'work', sessionsCompleted: 0, totalFocusSeconds: 0, lastActiveDate: today() };
      }
      return s;
    }
  } catch { /* use defaults */ }
  return { phase: 'work', sessionsCompleted: 0, totalFocusSeconds: 0, lastActiveDate: today() };
}

function loadProject(): PomodoroProject | null {
  try {
    const raw = localStorage.getItem(PROJECT_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* use null */ }
  return null;
}

function formatHHMM(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function phaseDurationSec(phase: PomodoroPhase, settings: PomodoroSettings): number {
  switch (phase) {
    case 'work': return settings.workDuration * 60;
    case 'shortBreak': return settings.shortBreakDuration * 60;
    case 'longBreak': return settings.longBreakDuration * 60;
  }
}

function playCompletionSound() {
  try {
    const ctx = new (window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)();

    // Play alarm pattern: 3 repetitions of ascending C5-E5-G5 chord, louder and longer
    const repetitions = 3;
    const gapBetweenReps = 0.55; // seconds between repetitions
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5

    for (let rep = 0; rep < repetitions; rep++) {
      const repOffset = rep * gapBetweenReps;
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const noteStart = ctx.currentTime + repOffset + i * 0.12;
        gain.gain.setValueAtTime(0, noteStart);
        gain.gain.linearRampToValueAtTime(0.5, noteStart + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.01, noteStart + 0.4);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(noteStart);
        osc.stop(noteStart + 0.4);
      });
    }
  } catch { /* silent fallback */ }
}

function sendBrowserNotification(phase: PomodoroPhase) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const title = phase === 'work' ? 'Work session complete!' : 'Break is over!';
  const body = phase === 'work' ? 'Time for a break.' : 'Ready to focus?';
  try {
    new Notification(title, { body, icon: '/favicon.ico', tag: 'pomodoro-timer' });
  } catch { /* silent fallback */ }
}

export function usePomodoro() {
  const [settings, setSettingsState] = useState<PomodoroSettings>(loadSettings);
  const [state, setStateRaw] = useState<PomodoroState>(loadState);
  const [running, setRunning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(() => phaseDurationSec(loadState().phase, loadSettings()));
  const [selectedProject, setSelectedProjectState] = useState<PomodoroProject | null>(loadProject);

  const targetEndRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const originalTitle = useRef(document.title);
  const sessionStartRef = useRef<Date | null>(null);
  const onWorkSessionCompleteRef = useRef<((info: WorkSessionInfo) => void) | null>(null);

  // Persist project selection
  const setSelectedProject = useCallback((p: PomodoroProject | null) => {
    setSelectedProjectState(p);
    if (p) {
      localStorage.setItem(PROJECT_KEY, JSON.stringify(p));
    } else {
      localStorage.removeItem(PROJECT_KEY);
    }
  }, []);

  // Register callback for work session completion
  const setOnWorkSessionComplete = useCallback((cb: ((info: WorkSessionInfo) => void) | null) => {
    onWorkSessionCompleteRef.current = cb;
  }, []);

  // Persist settings
  const setSettings = useCallback((s: PomodoroSettings) => {
    setSettingsState(s);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  }, []);

  // Persist state
  const setState = useCallback((updater: (prev: PomodoroState) => PomodoroState) => {
    setStateRaw((prev) => {
      const next = updater(prev);
      localStorage.setItem(STATE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // Clear tick interval
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Advance to next phase
  const advancePhase = useCallback((currentPhase: PomodoroPhase, completed: number, s: PomodoroSettings): { nextPhase: PomodoroPhase; autoStart: boolean } => {
    if (currentPhase === 'work') {
      const newCompleted = completed + 1;
      if (newCompleted % s.longBreakInterval === 0) {
        return { nextPhase: 'longBreak', autoStart: s.autoStartBreaks };
      }
      return { nextPhase: 'shortBreak', autoStart: s.autoStartBreaks };
    }
    return { nextPhase: 'work', autoStart: s.autoStartWork };
  }, []);

  // Handle phase completion
  const onPhaseComplete = useCallback(() => {
    clearTimer();
    targetEndRef.current = null;

    if (settings.soundEnabled) {
      playCompletionSound();
    }
    sendBrowserNotification(state.phase);

    // Fire work session complete callback
    if (state.phase === 'work' && onWorkSessionCompleteRef.current) {
      const now = new Date();
      const startTime = sessionStartRef.current || new Date(now.getTime() - settings.workDuration * 60 * 1000);
      onWorkSessionCompleteRef.current({
        project: selectedProject,
        startTime: formatHHMM(startTime),
        endTime: formatHHMM(now),
        date: today(),
        durationMinutes: settings.workDuration,
      });
    }
    sessionStartRef.current = null;

    const { nextPhase, autoStart } = advancePhase(state.phase, state.sessionsCompleted, settings);

    setState((prev) => ({
      ...prev,
      phase: nextPhase,
      sessionsCompleted: prev.phase === 'work' ? prev.sessionsCompleted + 1 : prev.sessionsCompleted,
      totalFocusSeconds: prev.phase === 'work' ? prev.totalFocusSeconds + phaseDurationSec('work', settings) : prev.totalFocusSeconds,
      lastActiveDate: today(),
    }));

    const nextDuration = phaseDurationSec(nextPhase, settings);
    setRemainingSeconds(nextDuration);

    if (autoStart) {
      targetEndRef.current = Date.now() + nextDuration * 1000;
      setRunning(true);
    } else {
      setRunning(false);
    }
  }, [clearTimer, settings, state.phase, state.sessionsCompleted, advancePhase, setState, selectedProject]);

  // Tick: compute remaining from wall-clock
  const tick = useCallback(() => {
    if (!targetEndRef.current) return;
    const diff = Math.round((targetEndRef.current - Date.now()) / 1000);
    if (diff <= 0) {
      setRemainingSeconds(0);
      onPhaseComplete();
    } else {
      setRemainingSeconds(diff);
    }
  }, [onPhaseComplete]);

  // Start/resume
  const start = useCallback(() => {
    targetEndRef.current = Date.now() + remainingSeconds * 1000;
    if (state.phase === 'work' && !sessionStartRef.current) {
      sessionStartRef.current = new Date();
    }
    setRunning(true);
  }, [remainingSeconds, state.phase]);

  // Pause
  const pause = useCallback(() => {
    clearTimer();
    // Freeze remaining from wall-clock
    if (targetEndRef.current) {
      const diff = Math.max(0, Math.round((targetEndRef.current - Date.now()) / 1000));
      setRemainingSeconds(diff);
    }
    targetEndRef.current = null;
    setRunning(false);
  }, [clearTimer]);

  // Reset current phase
  const reset = useCallback(() => {
    clearTimer();
    targetEndRef.current = null;
    sessionStartRef.current = null;
    setRunning(false);
    setRemainingSeconds(phaseDurationSec(state.phase, settings));
  }, [clearTimer, state.phase, settings]);

  // Skip to next phase
  const skip = useCallback(() => {
    clearTimer();
    targetEndRef.current = null;
    setRunning(false);

    const { nextPhase } = advancePhase(state.phase, state.sessionsCompleted, settings);

    // If skipping work, count partial focus time
    if (state.phase === 'work') {
      const full = phaseDurationSec('work', settings);
      const elapsed = full - remainingSeconds;
      setState((prev) => ({
        ...prev,
        phase: nextPhase,
        sessionsCompleted: prev.sessionsCompleted + 1,
        totalFocusSeconds: prev.totalFocusSeconds + elapsed,
        lastActiveDate: today(),
      }));
    } else {
      setState((prev) => ({ ...prev, phase: nextPhase, lastActiveDate: today() }));
    }

    setRemainingSeconds(phaseDurationSec(nextPhase, settings));
  }, [clearTimer, advancePhase, state.phase, state.sessionsCompleted, settings, remainingSeconds, setState]);

  // Switch phase manually
  const setPhase = useCallback((phase: PomodoroPhase) => {
    clearTimer();
    targetEndRef.current = null;
    sessionStartRef.current = null;
    setRunning(false);
    setState((prev) => ({ ...prev, phase, lastActiveDate: today() }));
    setRemainingSeconds(phaseDurationSec(phase, settings));
  }, [clearTimer, settings, setState]);

  // Reset settings to defaults
  const resetDefaults = useCallback(() => {
    setSettings({ ...DEFAULT_SETTINGS });
    // Also reset timer to new durations
    setRemainingSeconds(phaseDurationSec(state.phase, DEFAULT_SETTINGS));
    if (running) {
      pause();
    }
  }, [setSettings, state.phase, running, pause]);

  // Run interval when running
  useEffect(() => {
    if (running) {
      // If targetEnd not set (e.g., after autoStart), set it
      if (!targetEndRef.current) {
        targetEndRef.current = Date.now() + remainingSeconds * 1000;
      }
      timerRef.current = setInterval(tick, 250);
      return () => clearTimer();
    }
  }, [running, tick, clearTimer, remainingSeconds]);

  // Visibility change: recalculate on tab re-focus
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && running) {
        tick();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [running, tick]);

  // Update document title
  useEffect(() => {
    if (running || remainingSeconds < phaseDurationSec(state.phase, settings)) {
      const m = Math.floor(remainingSeconds / 60);
      const s = remainingSeconds % 60;
      const label = state.phase === 'work' ? 'Work' : state.phase === 'shortBreak' ? 'Break' : 'Long Break';
      document.title = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} - ${label}`;
    } else {
      document.title = originalTitle.current;
    }
    return () => {
      document.title = originalTitle.current;
    };
  }, [remainingSeconds, running, state.phase, settings]);

  // Recalculate remaining when settings change and timer is not running
  useEffect(() => {
    if (!running && targetEndRef.current === null) {
      setRemainingSeconds(phaseDurationSec(state.phase, settings));
    }
  }, [settings, state.phase, running]);

  const totalDuration = phaseDurationSec(state.phase, settings);

  // Toggle sound on/off
  const toggleSound = useCallback(() => {
    setSettings({ ...settings, soundEnabled: !settings.soundEnabled });
  }, [settings, setSettings]);

  // Request browser notification permission (call on user interaction)
  const requestNotificationPermission = useCallback(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  return {
    phase: state.phase,
    running,
    remainingSeconds,
    totalDuration,
    sessionsCompleted: state.sessionsCompleted,
    totalFocusSeconds: state.totalFocusSeconds,
    settings,
    setSettings,
    selectedProject,
    setSelectedProject,
    setOnWorkSessionComplete,
    start,
    pause,
    reset,
    skip,
    setPhase,
    resetDefaults,
    toggleSound,
    requestNotificationPermission,
    longBreakInterval: settings.longBreakInterval,
  };
}
